import { mensagemDeErro } from '@/lib/erros';
import { formatarHoraNoFuso } from '@/lib/fuso';
import { ApiError } from '@/lib/http';
import {
  type ConsultaChaveResposta,
  type EstadoCaptura,
  type ExecucaoCaptura,
  type ResultadoConsultaChave,
  type StatusExecucaoCaptura,
  type TipoExecucaoCaptura,
} from '@fiscoai/contracts';

export const ROTULO_STATUS_EXECUCAO: Record<StatusExecucaoCaptura, string> = {
  EXECUTANDO: 'Buscando',
  CONCLUIDA: 'Concluída',
  SEM_NOVIDADE: 'Sem novidade',
  BLOQUEADA: 'Bloqueada',
  SEM_CERTIFICADO: 'Sem certificado',
  CERTIFICADO_VENCIDO: 'Certificado vencido',
  ERRO: 'Erro',
};

export const ROTULO_TIPO_EXECUCAO: Record<TipoExecucaoCaptura, string> = {
  AGENDADA: 'Agendada',
  MANUAL: 'Manual',
  CONSULTA_CHAVE: 'Por chave',
};

const plural = (n: number, um: string, varios: string) => `${String(n)} ${n === 1 ? um : varios}`;

/** "a, b e c". */
function juntar(partes: readonly string[]): string {
  if (partes.length <= 1) return partes[0] ?? '';
  return `${partes.slice(0, -1).join(', ')} e ${partes.at(-1) ?? ''}`;
}

/** O que uma execução trouxe, em uma frase: "3 notas novas, 2 resumos e 1 evento". */
export function resumoExecucao(e: ExecucaoCaptura): string {
  if (e.status === 'SEM_NOVIDADE') return 'Nada novo na SEFAZ desde a última busca';
  if (e.status !== 'CONCLUIDA') return e.motivo ?? ROTULO_STATUS_EXECUCAO[e.status];
  const c = e.contadores;
  const partes = [
    c.importadas > 0 && plural(c.importadas, 'nota nova', 'notas novas'),
    c.resumos > 0 && plural(c.resumos, 'resumo aguardando XML', 'resumos aguardando XML'),
    c.eventos > 0 && plural(c.eventos, 'evento', 'eventos'),
    c.erros > 0 && plural(c.erros, 'erro', 'erros'),
  ].filter((p): p is string => typeof p === 'string');
  return partes.length > 0 ? juntar(partes) : 'Nada novo na SEFAZ desde a última busca';
}

/**
 * Mensagem de um erro da captura. Com o fuso da instalação, o 409 com `liberadaEm` vira a hora em
 * que a SEFAZ libera o CNPJ; sem ele (`null`, telas que não têm o estado da captura), vale o
 * `detail` do servidor, que já traz a hora no fuso da instalação. O resto (429 do limite, 422
 * desligada) usa sempre o `detail`.
 */
export function mensagemErroCaptura(erro: unknown, fuso: string | null): string {
  if (fuso && erro instanceof ApiError && erro.status === 409 && erro.problem.liberadaEm) {
    const hora = formatarHoraNoFuso(erro.problem.liberadaEm, fuso);
    return `A SEFAZ só libera este CNPJ às ${hora}. ${erro.problem.detail ?? ''}`.trim();
  }
  return mensagemDeErro(erro);
}

export type TomResultado = 'sucesso' | 'info' | 'alerta' | 'perigo';

export const TOM_RESULTADO_CHAVE: Record<ResultadoConsultaChave, TomResultado> = {
  JA_EXISTE: 'sucesso',
  XML_COMPLETO: 'sucesso',
  RESUMO: 'info',
  NAO_ENCONTRADA: 'alerta',
  FORA_DO_PRAZO: 'alerta',
  SEM_PERMISSAO: 'alerta',
  ERRO: 'perigo',
};

export const TITULO_RESULTADO_CHAVE: Record<ResultadoConsultaChave, string> = {
  JA_EXISTE: 'Já está no FiscoAI',
  XML_COMPLETO: 'XML completo recebido',
  RESUMO: 'Só o resumo por enquanto',
  NAO_ENCONTRADA: 'Nota não encontrada na SEFAZ',
  FORA_DO_PRAZO: 'Fora do prazo de 90 dias',
  SEM_PERMISSAO: 'Este CNPJ não pode baixar a nota',
  ERRO: 'A consulta não deu certo',
};

/** A nota pode ser aberta no FiscoAI? */
export function temNota(r: ConsultaChaveResposta): r is ConsultaChaveResposta & {
  documentoId: string;
} {
  return (r.resultado === 'JA_EXISTE' || r.resultado === 'XML_COMPLETO') && r.documentoId !== null;
}

/** Rota real do detalhe: a lista de documentos com a gaveta aberta. */
export const rotaDocumento = (id: string) => `/documentos?doc=${id}`;

const NUMERO = new Intl.NumberFormat('pt-BR');

/** NSU vem como texto com zeros ("000000000123456"): até 15 dígitos, cabe em BigInt sem perda. */
export function descreverNsu(ult: string, max: string | null): { nsu: string; fila: string } {
  const u = BigInt(ult || '0');
  const nsu = NUMERO.format(u);
  if (max === null) return { nsu, fila: 'ainda sem consulta' };
  const falta = BigInt(max) - u;
  return { nsu, fila: falta > 0n ? `${NUMERO.format(falta)} na fila` : 'em dia' };
}

/** Rejeições ligadas ao certificado (280–299) ou ao CNPJ/UF de quem consulta (593). */
const problemaDeCadastro = (c: number) => c === 593 || (c >= 280 && c <= 299);
/** Falha de rede (sem cStat) ou serviço paralisado (108 momentâneo, 109 sem previsão). */
const sefazInstavel = (c: number | null) => c === null || c === 108 || c === 109;

/**
 * O que fazer quando a última busca deu erro, a partir do cStat e de quando a captura tenta de
 * novo. `null` fora do erro.
 */
export function acaoSugerida(estado: EstadoCaptura, agora: number = Date.now()): string | null {
  if (estado.situacao !== 'ERRO') return null;
  const cstat = estado.ultimoCstat === null ? null : Number(estado.ultimoCstat);
  if (cstat !== null && problemaDeCadastro(cstat))
    return 'Confira o certificado A1 e a UF da empresa: a SEFAZ recusou quem consulta.';
  if (sefazInstavel(cstat)) {
    const tentativa = [estado.liberadaEm, estado.proximaExecucaoEm].find(
      (d): d is string => d !== null && Date.parse(d) > agora,
    );
    return tentativa
      ? `A SEFAZ está instável; a captura tenta de novo às ${formatarHoraNoFuso(tentativa, estado.fuso)}.`
      : 'A SEFAZ está instável; a captura tenta de novo na próxima busca.';
  }
  return 'Se o erro se repetir, confira o certificado e a agenda da empresa.';
}
